/*
 * Copyright 2023 Adobe
 * All Rights Reserved.
 *
 * NOTICE: All information contained herein is, and remains
 * the property of Adobe and its suppliers, if any. The intellectual
 * and technical concepts contained herein are proprietary to Adobe
 * and its suppliers and are protected by all applicable intellectual
 * property laws, including trade secret and copyright laws.
 * Dissemination of this information or reproduction of this material
 * is strictly forbidden unless prior written permission is obtained
 * from Adobe.
 */
import { useExtensions, TypedGuestConnection } from '@adobe/uix-host-react'
import { useEffect, useMemo, useState } from 'react'
import { GuestApis } from '@adobe/uix-core'
import { UixFrame, updatePageTitle } from './utils'
import { OrderViewButtonPageProps } from './props/OrderViewButtonProps'

export const OrderViewButtonPage = ({
  buttonId,
  extensionId,
  orderId,
  redirectUrl
}: OrderViewButtonPageProps) => {
  const orderIdParam = 'orderId'

  const { extensions } = useExtensions(() => ({
    updateOn: 'all',
    requires: {
      order: ['getOrderViewButtons']
    },
    provides: {
      integration: {
        getCommerceHost() {
          return window.location.host
        }
      }
    }
  }))

  const [actionUrl, setActionUrl] = useState<string>()

  const extension: TypedGuestConnection<GuestApis> | null = useMemo(
    () => extensions.find((item) => item.id === extensionId) ?? null,
    [extensions, extensionId]
  )

  useEffect(() => {
    extension &&
      extension.apis.order
        .getOrderViewButtons()
        .then((orderViewButtons: Array<any>) => {
          const button = orderViewButtons.find(
            (button: any) => button['buttonId'] === buttonId
          )
          updatePageTitle(button.label)
          setActionUrl(
            `${extension.url.origin}/${button.path}?${orderIdParam}=${orderId}`
          )
        })
  }, [extension, buttonId, orderId])

  if (!extension || !actionUrl) {
    return <></>
  }

  return (
    <UixFrame
      url={actionUrl}
      extensionId={extension?.id}
      redirectUrl={redirectUrl}
    />
  )
}
